import { useState } from 'react';
import { KeysType, getAbTestData } from '@/common/abTest';
import { KSwitchKeyType, getKSwitch } from '@/common/kswitch';
import { isIOS, localStorage } from '@kibt/krn-utils';
import { versionCompare } from '@kibt/web-utils/version';
import { appVersion } from '@/common/device-info';
import { hashCode } from '@/common/utils/hashCode';

// ab处理器，通过AB接口获取AB信息，将ab返回的结果转换成业务需要的对象
const abApiHandlers: Array<{
    keys: KeysType;
    handler: (res: {
        [key: string]: {
            //
            value: any;
            experimentName?: string;
            groupName?: string;
        };
    }) => Record<string, any>;
}> = [];

abApiHandlers.push({
    keys: {
        if_product_detail_addon: 'bool', // 商详页是否支持凑单楼层
        if_product_addon_spec_modifiabe: 'bool', // 商详页凑单楼层规格筛选半屏页可修改
    },
    handler: (res) => {
        return {
            if_product_detail_addon: res.if_product_detail_addon?.value || false,
            if_product_addon_spec_modifiabe: res.if_product_addon_spec_modifiabe?.value || false,
        };
    },
});

// 二次引导支付
abApiHandlers.push({
    keys: {
        Order_entry_education: 'bool', // 支付结果页的订单入口教育+订单详情页的订单入口教育
        Countdown: 'bool', // 订单列表页的倒计时+订单详情页的倒计时
    },
    handler: (res) => {
        return res;
    },
});

// 提单页V2.0实验ab
abApiHandlers.push({
    keys: { if_hit_orderpage_v2: 'bool' },
    handler: (res) => res,
});

/**
 * 提单页动态运费券商详页利益点透出
 * https://docs.corp.kuaishou.com/d/home/fcABDMOM4-NicB_92JHFF2mJX
 */
abApiHandlers.push({
    keys: { if_product_details_dynamic_free_shipping_coupon: 'bool' },
    handler: (res) => res,
});

// 通过kswitch获取AB信息
const kswitchAbHandlers: Array<{
    keys: KSwitchKeyType[];
    handler: (res: { [key: string]: any }) => Record<string, any>;
}> = [];

kswitchAbHandlers.push({
    keys: [
        {
            key: 'single_spec_show', // 单规格商品件数是否透出 0：不透出，1：透出在主图下面，2：透出在商品标题下面
        },
        {
            key: 'if_spec_num_up', // 规格筛选半屏页数量是否上移 false or true
        },
    ],
    handler: (res) => {
        return res;
    },
});

kswitchAbHandlers.push({
    keys: [
        {
            key: 'ifABSingleSpecificationRemove',
        },
        {
            key: 'ifABnoSpecificationPictureRemove',
        },
        {
            key: 'detailsPageLayoutScheme',
        },
        {
            key: 'detailSwiperIamgeAB',
        },
        {
            key: 'if_installment_amount_first',
        },
        {
            key: 'ProductDetailTransRegional',
        },
        {
            key: 'if_specification_picture_opt',
        },
    ],
    handler: (res) => {
        const ifABSingleSpecificationRemove = res.ifABSingleSpecificationRemove || false;
        const ifABnoSpecificationPictureRemove = res.ifABnoSpecificationPictureRemove || false;
        const layoutCase = res.detailsPageLayoutScheme || 0;
        const isABdetailSwiperIamgePartB = res.detailSwiperIamgeAB || false;
        const ifInstallmentAmountFirst = res.if_installment_amount_first || false;
        const ifProductDetailTransRegional = res.ProductDetailTransRegional || false;
        const ifSpecificationPictureOpt = res.if_specification_picture_opt || false;

        return {
            ifABSingleSpecificationRemove,
            ifABnoSpecificationPictureRemove,
            layoutCase,
            isABdetailSwiperIamgePartB,
            ifInstallmentAmountFirst,
            ifProductDetailTransRegional,
            ifSpecificationPictureOpt,
            getExpValue: true,
        };
    },
});

kswitchAbHandlers.push({
    keys: [
        {
            key: 'if_service_guarantee',
        },
        {
            key: 'service_guarantee_style',
        },
        {
            key: 'link_service_guarantee',
        },
    ],
    handler: (res) => {
        return {
            ifServiceGuarantee: res.if_service_guarantee || false,
            serviceGuaranteeStyle: res.service_guarantee_style || 1,
            link_service_guarantee: res.link_service_guarantee,
        };
    },
});

kswitchAbHandlers.push({
    keys: [{ key: 'cartOptimization' }],
    handler: (res) => ({ cartOptimization: res.cartOptimization }),
});

kswitchAbHandlers.push({
    keys: [
        {
            key: 'add_on_coupon_type',
        },
        {
            key: 'add_on_btn_type',
        },
    ],
    handler: (res) => {
        const isIOSAndVersionInvalid = isIOS && versionCompare(appVersion, '9.9.40') === -1;
        const add_on_coupon_type = isIOSAndVersionInvalid ? 0 : res[0]?.value ?? 0;
        const add_on_btn_type = isIOSAndVersionInvalid ? 0 : res[1]?.value ?? 0;

        return {
            addOnCouponType: add_on_coupon_type,
            addOnBtnType: add_on_btn_type,
        };
    },
});

kswitchAbHandlers.push({
    keys: [{ key: 'product_detail_bottom_benefits' }],
    handler: (res) => {
        // 凑单工具里无商品时，显示空的凑单工具条
        const result = res.product_detail_bottom_benefits || '';
        // const result = 'store_free_shipping,shipping_coupon,local_product,flash_sale';
        const copedResult = {};
        if (result) {
            result.split(',').forEach((key) => {
                copedResult[key] = true;
            });
        }
        return copedResult;
    },
});

kswitchAbHandlers.push({
    keys: [{ key: 'if_detail_product_list_show_flash_sale' }],
    handler: (res) => {
        return res;
    },
});

kswitchAbHandlers.push({
    keys: [{ key: 'show_price_used_coupon_style' }],
    handler: (res) => {
        return { showPriceUsedCouponStyle: res.show_price_used_coupon_style || false };
    },
});

kswitchAbHandlers.push({
    keys: [
        {
            key: 'product_coupon_priority', // 是否优先展示商品券
        },
        {
            key: 'coupon_use_prompt', // 是否使用优惠券催用样式
        },
    ],
    handler: (res) => {
        return {
            productCouponPriority: res.product_coupon_priority || false,
            couponUsePrompt: res.coupon_use_prompt || false,
        };
    },
});

kswitchAbHandlers.push({
    keys: [
        {
            key: 'cart_make_up_order_v2',
        },
        {
            key: 'cart_check_out',
        },
    ],
    handler: (res) => {
        return res;
    },
});

kswitchAbHandlers.push({
    keys: [
        {
            key: 'cartEditSpec',
        },
    ],
    handler: (res) => {
        return res;
    },
});

// 购物车相关ab
kswitchAbHandlers.push({
    keys: [
        {
            key: 'cart_benefits', // 购物车利益点透出
        },
        {
            key: 'cart_interaction', // 购物车结算交互
        },
        {
            key: 'message_contact_undertaking', // 消息触达承接交互
        },
    ],
    handler: (res) => {
        return res;
    },
});

// 小黄车ab
kswitchAbHandlers.push({
    keys: [
        {
            key: 'new_coupon_label', // 券样式实验
        },
        {
            key: 'add_item_guide', // 是否展示凑单浮层
        },
    ],
    handler: (res) => res,
});

const buildHandler = (handlers) => (res) => {
    if (!res) return {};

    return handlers.reduce((pre, curr) => {
        return Object.assign(pre, curr.handler(res));
    }, {});
};

// ab接口返回的数据cache
let cacheState;

// 合并ab结果的方法
const doHandleAbRes = buildHandler(abApiHandlers);
const doHandleKSwitchRes = buildHandler(kswitchAbHandlers);

const getAbInfo = async () => {
    const startTime = Date.now();

    let abRes = {};
    const res = {};

    let kswitchHandleRes: any = {};
    let kswitchAbResObj = {};

    try {
        // 合并ab keys
        const keys = abApiHandlers.reduce((pre, curr) => {
            return Object.assign(pre, curr.keys);
        }, {});
        // 合并kswitch keys
        const kswitchKeys = kswitchAbHandlers.reduce((pre, curr) => {
            return [...pre, ...curr.keys];
        }, []);
        // key拼接会太长了，将拼接后的key转成hashcode
        const cacheKeyStr = Object.keys(keys).join('_') + kswitchKeys.join('_');
        const cacheKey = `Ecomm_Global_Ab_Result_${hashCode(cacheKeyStr)}`;
        const storeRes = await localStorage.getItem(cacheKey);

        //使用缓存
        if (storeRes) {
            const objRes = JSON.parse(storeRes as string) as any;
            abRes = objRes.apiResult;
            kswitchHandleRes = objRes.kswitchResult;
        }

        // 接口获取并缓存
        const [apiAbRes, kswitchAbRes] = await Promise.all([getAbTestData(keys), getKSwitch(kswitchKeys)]);
        console.log(`[FirstScreen Ab] api ab result: `, apiAbRes);
        abRes = apiAbRes;
        // 转换结果
        abRes = doHandleAbRes(apiAbRes);

        // 将数组结构转换成map结构
        kswitchAbResObj = kswitchAbRes.reduce((pre, curr) => {
            return Object.assign(pre, { [curr.key]: curr.value });
        }, {});
        console.log(`[FirstScreen Ab] kswitch ab result: `, kswitchAbRes);
        // 转换结果
        kswitchHandleRes = doHandleKSwitchRes(kswitchAbResObj);

        setTimeout(() => {
            // 保存到localstore中
            localStorage.setItem(
                cacheKey,
                JSON.stringify({
                    apiResult: abRes,
                    kswitchResult: kswitchHandleRes,
                }),
            );
        }, 5000);
    } catch (err) {
        console.log('[FirstScreen Ab] ab error: ', err);
    }

    console.log(`[FirstScreen Ab] load ab cost time: ${Date.now() - startTime}ms`);
    // 设置cache，标记状态
    const state = {
        // 业务转换后的key,value对
        ...abRes,
        ...kswitchHandleRes,
        // 原始的ab结果
        originApiAbInfo: res,
        originKswitchAbInfo: kswitchAbResObj,
        isAbLoading: false,
    };
    cacheState = state;
    return state;
};

export const abPromise = getAbInfo();

/**
 * 首屏的AB接口调用太多了，需要收拢一下，如：商详首屏编写这个hooks当天有6个AB
 */
export function useFirstScreenAB<
    T extends {
        // ab是否加载中
        isAbLoading?: boolean;
        // ab内容的key value对
        [key: string]: any;
    },
>() {
    // 生命State的时候就从cache初始化AB信息
    const [state, setState] = useState<T>(cacheState ? Object.assign({}, cacheState, { isAbLoading: false }) : { isAbLoading: true });
    useState(() => {
        if (!cacheState) {
            abPromise.then((res) => {
                setState(Object.assign({}, res, { isAbLoading: false }));
            });
        }
    });

    return state;
}
